import useAPI from '@/hooks/use-api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const useApiMutation = <T, V = T>({
  endpoint,
  customQueryKey,
  contentType = 'application/json',
}: {
  endpoint: string
  customQueryKey?: (string | unknown)[]
  contentType?: 'application/json' | 'multipart/form-data'
}) => {
  const { post, patch, remove, removeMultiple, removeAll } = useAPI<T | V>()
  const queryClient = useQueryClient()
  const queryKey = customQueryKey ? [...customQueryKey] : [endpoint]

  // Create function
  const createFn = async (data: T) => {
    const response = await post(endpoint, data, contentType)
    if (!response?.status) throw new Error(response?.message)
    return response
  }

  // Update function
  const updateFn = async (data: V) => {
    const response = await patch(endpoint, data, contentType)
    if (!response?.status) throw new Error(response?.message ?? 'An error occurred')
  }

  // Delete function
  const deleteFn = async (id: string) => {
    const response = await remove(endpoint, id)
    if (!response?.status) throw new Error(response?.message)
  }

  // Delete all function
  const deleteAllFn = async () => {
    const response = await removeAll(endpoint)
    if (!response?.status) throw new Error(response?.message)
  }

  // Delete multiple function
  const deleteMultipleFn = async (ids: string[]) => {
    const response = await removeMultiple(endpoint, ids)
    if (!response?.status) throw new Error(response?.message)
  }

  // Mutation hook for create
  const createMutation = useMutation({
    mutationFn: createFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey, exact: false })
    },
  })

  // Mutation hook for update
  const updateMutation = useMutation({
    mutationFn: updateFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey, exact: false })
    },
  })

  // Mutation hook for delete
  const deleteMutation = useMutation({
    mutationFn: deleteFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey, exact: false })
    },
  })

  // Mutation hook for delete
  const deleteAllMutation = useMutation({
    mutationFn: deleteAllFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey, exact: false })
    },
  })

  // Mutation hook for delete multiple
  const deleteMultipleMutation = useMutation({
    mutationFn: deleteMultipleFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey, exact: false })
    },
  })

  return { createMutation, updateMutation, deleteMutation, deleteMultipleMutation, deleteAllMutation }
}

export default useApiMutation
